import { useState } from 'react';
import { useT } from '@hana/i18n';
import { IconArrowUpRight } from '@tabler/icons-react';
import type { Claim, ClaimState, ContainerFree } from './types';
import { freeLeft } from './logic';
import s from './importDock.module.css';

// Bảng phải của Mint-Import-1920 (Phân tích · Việc · Thông báo). money = hide nên KPI "Phí DEM/DET bán" không dựng. Chỉ đọc.

type DockTab = 'an' | 'task' | 'noti';

const TASKS: ReadonlyArray<readonly [string, string, string, string, boolean]> = [
  ['DMD', 'Báo khách free time FBIU5484924', 'Hôm nay 16:00', 'THUỶ', true],
  ['CLM', 'Thông báo tổn thất CLM-2610-07 cho KMTC', '24/Oct/2026 17:00', 'MAI', false],
  ['CDR', 'Đòi MSC hoàn cược MEDU 410028-5', 'Quá 9 ngày', 'HÀ', true],
  ['K4', 'Lệch nhiệt độ SZLU2019991', 'Ngay', 'MAI', true],
  ['DMD', 'DET OOLU0320399 quá hạn — xác nhận ngày', 'Hôm nay', 'THUỶ', false],
  ['ADV', 'Thu cược khách VJTrading', '25/Oct/2026', 'LIÊN', false],
];
const NOTIS: ReadonlyArray<readonly [string, string, string, boolean]> = [
  ['ePort: FBIU5484924 đã lấy khỏi cảng 09:12', 'vừa xong', 'var(--d-blue)', true],
  ['Khách NDP đã xác nhận phí lưu bãi lạnh', '12 phút trước', 'var(--d-green)', true],
  ['HANA Mov: thiếu ảnh nhiệt độ YMLU5438708', '40 phút trước', 'var(--d-amber)', false],
  ['CCF: tờ khai 105928817420 phân luồng đỏ', '08:05', 'var(--err)', false],
];
const LANES: ReadonlyArray<readonly [string, number, string]> = [['Xanh', 31, 'var(--d-green)'], ['Vàng', 9, 'var(--d-amber)'], ['Đỏ', 4, 'var(--err)'], ['Soi chiếu', 2, 'var(--d-violet)']];
const CLAIMS: ReadonlyArray<readonly [ClaimState, string, string]> = [['open', 'Đang mở', 'var(--d-blue)'], ['chase', 'Đang đòi bồi thường', 'var(--d-amber)'], ['bod', 'Chờ BOD', 'var(--d-violet)'], ['closed', 'Đã đóng', 'var(--d-green)']];
const CARRIERS: ReadonlyArray<readonly [string, number]> = [['KMTC', 4], ['HMM', 3], ['ONE', 2], ['CMA CGM', 2], ['MSC', 1]];
const CC = 2 * Math.PI * 52;

interface Props {
  containers: readonly ContainerFree[];
  claims: readonly Claim[];
  onClose: () => void;
  onOpenClaims: () => void;
}

export function ImportDock({ containers, claims, onClose, onOpenClaims }: Props) {
  const t = useT();
  const [tab, setTab] = useState<DockTab>('an');
  const [done, setDone] = useState<Record<string, true>>({});
  const soon = containers.filter((c) => freeLeft(c) <= 2);
  const kpi: ReadonlyArray<readonly [string, string, string, string]> = [
    ['Sắp phát sinh phí', '3', 'cont', 'var(--warn)'],
    ['Đang quá free time', '3', 'cont', 'var(--err)'],
    ['Chờ xác nhận ngày', '2', 'cont', 'var(--tx)'],
    ['Claim đang mở', '2', 'hồ sơ', 'var(--tx)'],
  ];
  const laneTot = LANES.reduce((a, x) => a + x[1], 0);
  let acc = 0;
  const arcs = LANES.map(([k, n, c]) => {
    const L = (n / laneTot) * CC;
    const g = L > 4 ? 3 : 0;
    const arc = { k, n, c, p: `${Math.round((n / laneTot) * 100)}%`, dash: `${Math.max(0, L - g).toFixed(1)} ${(CC - L + g).toFixed(1)}`, off: (-acc).toFixed(1) };
    acc += L;
    return arc;
  });
  const clm = CLAIMS.map(([k, label, c]) => ({ k: label, n: claims.filter((x) => x.st === k).length, c }));
  const by: Record<string, number> = {};
  for (const x of claims) by[x.party] = (by[x.party] ?? 0) + 1;
  const tabs: ReadonlyArray<{ key: DockTab; label: string; n?: number }> = [
    { key: 'an', label: 'Phân tích' },
    { key: 'task', label: 'Việc', n: TASKS.length },
    { key: 'noti', label: 'Thông báo', n: NOTIS.length },
  ];
  return (
    <aside className={s.dock}>
      <div className={s.head}>
        <div role="tablist" aria-label="Bảng bên phải" className={s.tabs}>
          {tabs.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`${s.tab} ${tab === t.key ? s.on : ''}`} onClick={() => setTab(t.key)}>
              {t.label} {t.n !== undefined && <span className={s.lb}>{t.n}</span>}
            </button>
          ))}
        </div>
        <button type="button" className={s.close} aria-label={t('imp.closePanel')} onClick={onClose}>×</button>
      </div>
      {tab === 'an' && (
        <>
          <section className={s.card}>
            <h2>{t('imp.month')}</h2>
            <div className={s.kpi}>
              {kpi.map(([k, v, u, c]) => (
                <div key={k}>
                  <div className={s.lb12}>{k}</div>
                  <div className={s.kv}><b style={{ color: c }}>{v}</b> <span className={s.lb12}>{u}</span></div>
                </div>
              ))}
            </div>
          </section>
          <section className={s.card}>
            <div className={s.sh}><h2>{t('imp.soonFee')}</h2><span className={s.lb12}>{t('imp.daysUsed')}</span></div>
            <div role="img" aria-label={`Free time cont sắp phát sinh phí: ${soon.map((c) => `${c.cont} ${freeLeft(c) < 0 ? `quá ${-freeLeft(c)}` : `còn ${freeLeft(c)}`} ngày`).join(', ')}`}>
              {soon.map((c, i) => {
                const l = freeLeft(c);
                const nT = Math.max(c.freeCustomer, c.used);
                return (
                  <div key={c.cont} className={s.soon} style={{ borderTopWidth: i ? 1 : 0 }}>
                    <div className={s.soonH}>
                      <span className={s.soonK}><b>{c.cont}</b> <span className={s.lb}>· {c.cus}</span></span>
                      <span className={s.chip} style={{ color: l < 0 ? 'var(--err)' : 'var(--warn)', background: l < 0 ? 'var(--err-bg)' : 'var(--warn-bg)' }}>{l < 0 ? `quá ${-l} ngày` : `còn ${l} ngày`}</span>
                    </div>
                    <div className={s.ticks}>
                      {Array.from({ length: nT }, (_, j) => (
                        <i key={j} style={{ background: j < Math.min(c.used, c.freeCustomer) ? (l < 0 ? 'var(--err)' : 'var(--d-amber)') : j < c.freeCustomer ? 'var(--line)' : 'var(--err)' }} />
                      ))}
                    </div>
                    <div className={s.use}>{c.kind} · đã dùng {c.used}/{c.freeCustomer} ngày free khách · {c.start}</div>
                  </div>
                );
              })}
            </div>
          </section>
          <section className={s.card}>
            <div className={s.sh}><h2>{t('imp.demByCarrier')}</h2><span className={s.lb12}>{t('imp.contOct')}</span></div>
            {CARRIERS.map(([k, v]) => (
              <div key={k} className={s.car}>
                <span className={s.tx2}>{k}</span>
                <div className={s.track}><div style={{ width: `${Math.round((v / CARRIERS[0]![1]) * 100)}%` }} /></div>
                <b>{v}</b>
              </div>
            ))}
          </section>
          <section className={s.card}>
            <h2 className={s.h2m}>{t('imp.customsFlow')}</h2>
            <div className={s.donut}>
              <svg width="132" height="132" viewBox="0 0 132 132" role="img" aria-label={`Luồng hải quan tháng ${laneTot} tờ khai: ${arcs.map((x) => `${x.k} ${x.n} (${x.p})`).join(', ')}`}>
                <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="16" />
                {arcs.map((a) => (
                  <circle key={a.k} cx="66" cy="66" r="52" fill="none" stroke={a.c} strokeWidth="16" strokeDasharray={a.dash} strokeDashoffset={a.off} transform="rotate(-90 66 66)" />
                ))}
                <text x="66" y="68" textAnchor="middle" fontSize="22" fontWeight="550" fill="var(--tx)">{laneTot}</text>
                <text x="66" y="85" textAnchor="middle" fontSize="11" fill="var(--lb)">{t('imp.decl')}</text>
              </svg>
              <div className={s.legend}>
                {arcs.map((a) => (
                  <span key={a.k}><i style={{ background: a.c }} /><span className={s.grow}>{a.k}</span><b>{a.n}</b><span className={s.pc}>{a.p}</span></span>
                ))}
              </div>
            </div>
          </section>
          <section className={s.card}>
            <div className={s.sh}>
              <h2>{t('imp.incidents')}</h2>
              <button type="button" className={s.go} onClick={onOpenClaims}>
                <IconArrowUpRight size={14} aria-hidden="true" />
                {t('imp.openTab')}
              </button>
            </div>
            <div className={s.stack} role="img" aria-label={`Sự cố và claim ${claims.length} hồ sơ: ${clm.map((x) => `${x.k} ${x.n}`).join(', ')}`}>
              {clm.map((x) => <div key={x.k} style={{ flex: `${x.n} 1 0`, background: x.c }} />)}
            </div>
            <div className={s.clmL}>
              {clm.map((x) => <span key={x.k}><i style={{ background: x.c }} /><span className={s.grow}>{x.k}</span><b>{x.n}</b></span>)}
            </div>
            <div className={s.lb12} style={{ marginTop: 8 }}>Bên chịu: {Object.entries(by).map(([k, n]) => `${k} ${n}`).join(' · ')}</div>
          </section>
        </>
      )}
      {tab === 'task' &&
        TASKS.map((x) => (
          <label key={x[1]} className={s.task} style={{ opacity: done[x[1]] ? 0.5 : 1 }}>
            <input type="checkbox" aria-label={`Xong: ${x[1]}`} checked={!!done[x[1]]} onChange={() => setDone((d) => (d[x[1]] ? {} : { ...d, [x[1]]: true }))} />
            <div className={s.taskB}>
              <div className={s.taskH}>
                <span className={s.code}>{x[0]}</span>
                <span style={{ fontWeight: 500, textDecoration: done[x[1]] ? 'line-through' : 'none' }}>{x[1]}</span>
              </div>
              <div className={s.lb12} style={{ marginTop: 3 }}><span style={{ color: x[4] ? 'var(--err)' : 'var(--lb)' }}>{x[2]}</span> · {x[3]}</div>
            </div>
          </label>
        ))}
      {tab === 'noti' &&
        NOTIS.map((n, i) => (
          <div key={n[0]} className={s.noti} style={{ borderTopWidth: i ? 1 : 0 }}>
            <span className={s.nDot} style={{ background: n[2] }} />
            <div>
              <div style={{ fontWeight: n[3] ? 600 : 450 }}>{n[0]}</div>
              <div className={s.lb12} style={{ marginTop: 2 }}>{n[1]}</div>
            </div>
          </div>
        ))}
    </aside>
  );
}
